import { ReactElement } from "react"

import {
    ApplicationStatuses,
    InstallerApplicationStatuses,
} from "../../../constants/enums"
import { UNKNOWN_STATUS } from "../../../constants/generalConstants"
import { arrayOnCondition } from "../../../utils/general/generalUtils"
import AppStepper from "../../dataDisplay/stepper/appStepper"

const APPLICAITN_CREATED_AND_APPROVED = "Application Created and Approved"
const APPLICATION_AMENDED = "Application Amended"
const APPLICATION_CREATED = "Application Created"
const CUT_OUT_SCANNED = "Cut-out Image Scanned"
const ENA_ASSESSED = "Assessed by Connect Direct"
const APPLICATION_APPROVED = "Application Approved"
const CX_SUBMITTED = "Commissioning Information Submitted"
const CX_APPROVED = "Commissioning Information Approved"
const CX_SENT_BACK = "Commissioning Sent Back"

const ESCALATED_STEP_LABELS = (dno: string): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    "Installer notified of outcome",
]

const RE_ESCALATED_STEP_LABELS = (dno: string): string[] => [
    APPLICAITN_CREATED_AND_APPROVED,
    APPLICATION_AMENDED,
    CUT_OUT_SCANNED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    "Installer notified of outcome",
]

const AUTO_APPROVED_STEP_LABELS = (isGen: boolean): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    "Approved by Connect Direct",
    "DNO and installer notified of outcome",
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const RE_AUTO_APPROVED_STEP_LABELS = (isGen: boolean): string[] => [
    APPLICAITN_CREATED_AND_APPROVED,
    APPLICATION_AMENDED,
    CUT_OUT_SCANNED,
    "Approved by Connect Direct",
    "DNO and installer notified of outcome",
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const DNO_APPROVED_STEP_LABELS = (dno: string, isGen: boolean): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    "Approved by DNO & installer notified",
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const DNO_APPROVED_WITH_LIMIT_STEP_LABELS = (
    dno: string,
    isGen: boolean
): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    "Approved with limit by DNO & Installer notified",
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const DNO_FURTHER_INFO_REQUIRED_STEP_LABELS = (dno: string): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    "Reviewed by DNO & installer notified",
]

const FALLBACK_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    "Error retrieving status",
]

const CX_SUBMITTED_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    APPLICATION_APPROVED,
    CX_SUBMITTED,
    CX_APPROVED,
]

const CX_SENT_BACK_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    APPLICATION_APPROVED,
    CX_SUBMITTED,
    CX_SENT_BACK,
    "Commissioning Update Submitted",
]

const CX_APPROVED_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    APPLICATION_APPROVED,
    CX_SUBMITTED,
    CX_APPROVED,
]

type TStepper = {
    steps: string[]
    currentStep: number
}

export interface IStepperRecognisedDispatcher {
    [key: string]: TStepper
}

const STEPPER_DISPATCHER = (
    dno: string,
    isGeneration: boolean
): IStepperRecognisedDispatcher => ({
    [ApplicationStatuses.escalatedToDno]: {
        steps: ESCALATED_STEP_LABELS(dno),
        currentStep: 4,
    },
    [ApplicationStatuses.autoApproved]: {
        steps: AUTO_APPROVED_STEP_LABELS(isGeneration),
        currentStep: 5,
    },
    [ApplicationStatuses.dnoApproved]: {
        steps: DNO_APPROVED_STEP_LABELS(dno, isGeneration),
        currentStep: 5,
    },
    [ApplicationStatuses.dnoApprovedWithLimit]: {
        steps: DNO_APPROVED_WITH_LIMIT_STEP_LABELS(dno, isGeneration),
        currentStep: 5,
    },
    [ApplicationStatuses.dnoFurtherInfoRequired]: {
        steps: DNO_FURTHER_INFO_REQUIRED_STEP_LABELS(dno),
        currentStep: 5,
    },
    [ApplicationStatuses.cxSubmitted]: {
        steps: CX_SUBMITTED_STEP_LABELS,
        currentStep: 4,
    },
    [ApplicationStatuses.cxUpdateRequired]: {
        steps: CX_SENT_BACK_STEP_LABELS,
        currentStep: 5,
    },
    [ApplicationStatuses.cxAccepted]: {
        steps: CX_APPROVED_STEP_LABELS,
        currentStep: 5,
    },
    [InstallerApplicationStatuses.reAutoApproved]: {
        steps: RE_AUTO_APPROVED_STEP_LABELS(isGeneration),
        currentStep: 5,
    },
    [InstallerApplicationStatuses.reEscalatedToDno]: {
        steps: RE_ESCALATED_STEP_LABELS(dno),
        currentStep: 4,
    },
    [UNKNOWN_STATUS]: {
        steps: FALLBACK_STEP_LABELS,
        currentStep: 4,
    },
})

interface DnoApplicationProgressStepperProps {
    status: ApplicationStatuses | string
    dno: string
    isGeneration: boolean
}
const DnoApplicationProgressStepper = ({
    status,
    dno,
    isGeneration,
}: DnoApplicationProgressStepperProps): ReactElement => {
    const stepperConfig =
        STEPPER_DISPATCHER(dno, isGeneration)[status] ||
        STEPPER_DISPATCHER("", isGeneration)[UNKNOWN_STATUS]
    return (
        <AppStepper
            stepLabels={stepperConfig.steps}
            currentStepIndex={stepperConfig.currentStep}
        />
    )
}

export default DnoApplicationProgressStepper
